// Downscale + re-encode a captured receipt photo before it is queued, so
// job-site LTE stays fast and R2 usage stays trivial (target ~500KB-1MB).
// Duplicated verbatim from TimeTrakrDashboard's src/lib/compressImage.ts —
// same browser APIs, no framework-specific code. Two separate repos with no
// shared package, so this is a deliberate copy; keep the two in step.
export async function compressImageToJpeg(
  file: File,
  { maxDimension = 2000, quality = 0.8 }: { maxDimension?: number; quality?: number } = {}
): Promise<File> {
  if (typeof createImageBitmap === 'undefined' || typeof document === 'undefined') {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // Fall back to the original rather than block capture on a decode failure.
  }

  const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) return file;

  return new File([blob], file.name.replace(/\.\w+$/, '.jpg') || 'receipt.jpg', { type: 'image/jpeg' });
}
